import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Package, Filter, X, LayoutDashboard, History,
  ShoppingCart, Factory, ClipboardCheck, Truck, CheckCircle2,
  ArrowRight, SlidersHorizontal, Clock, Moon, AlignJustify, ShieldAlert, Zap,
} from "lucide-react";
import { DataStateContext, FilterContext, RoleContext, AuthContext, ClientsContext, ClientGroupsContext, ThemeContext } from "@/App";
import { useTableDensity } from "@/hooks/useTableDensity";

const STAGE_PATHS = {
  "PO Received": "/po-received", "Procurement": "/procurement", "Manufacturing": "/manufacturing",
  "Quality Control": "/qc-check", "Dispatch": "/dispatch", "Completed": "/completed",
};
const STAGE_COLORS = {
  "PO Received": "#3b82f6", "Procurement": "#f97316", "Manufacturing": "#a855f7",
  "Quality Control": "#14b8a6", "Dispatch": "#f59e0b", "Completed": "#94a3b8",
};
const STAGE_SHORTCUT = { h: "/", r: "/po-received", p: "/procurement", m: "/manufacturing", q: "/qc-check", d: "/dispatch", c: "/completed", l: "/changelog" };

const GOTO_ITEMS = [
  { label: "Dashboard",     path: "/",             icon: LayoutDashboard, stage: null,              hint: "H" },
  { label: "PO Received",   path: "/po-received",  icon: Package,         stage: "PO Received",     hint: "R" },
  { label: "Procurement",   path: "/procurement",  icon: ShoppingCart,    stage: "Procurement",     hint: "P" },
  { label: "Manufacturing", path: "/manufacturing",icon: Factory,         stage: "Manufacturing",   hint: "M" },
  { label: "QC Check",      path: "/qc-check",     icon: ClipboardCheck,  stage: "Quality Control", hint: "Q" },
  { label: "Dispatch",      path: "/dispatch",     icon: Truck,           stage: "Dispatch",        hint: "D" },
  { label: "Completed",     path: "/completed",    icon: CheckCircle2,    stage: "Completed",       hint: "C" },
  { label: "History Log",   path: "/changelog",    icon: History,         stage: null,              hint: "L" },
];
const FILTER_COMMANDS = [
  { label: "Clear all filters", hint: "X", run: (s) => s({ clients: null, priority: "all", flag: null }) },
];

const ATTENTION_COMMANDS = [
  { id: "blocked",  label: "Show blocked items",     hint: "B", isOn: (f) => f.flag === "blocked",      apply: (f) => ({ ...f, flag: f.flag === "blocked" ? null : "blocked" }) },
  { id: "overdue",  label: "Show overdue",           hint: "O", isOn: (f) => f.flag === "overdue",      apply: (f) => ({ ...f, flag: f.flag === "overdue" ? null : "overdue" }) },
  { id: "expedite", label: "Expedite priority only", hint: "E", isOn: (f) => f.priority === "Expedite", apply: (f) => ({ ...f, priority: f.priority === "Expedite" ? "all" : "Expedite" }) },
];

const RECENT_KEY = (uid) => `rl_recent_${uid || "anon"}`;
const RECENT_MAX = 5;

function resolveClientBadge(client, clientGroups) {
  if (!client) return { logo_svg: null, color: "#94a3b8" };
  if (client.logo_svg) return { logo_svg: client.logo_svg, color: client.color };
  const groups = clientGroups || [];
  const sub = groups.find(g => g.type === "subgroup" && (g.client_ids || []).includes(client.id));
  if (sub && sub.logo_svg) return { logo_svg: sub.logo_svg, color: client.color || sub.color };
  const grp = groups.find(g => g.type === "group" && ((g.client_ids || []).includes(client.id) || (sub && sub.parent_id === g.id)));
  if (grp && grp.logo_svg) return { logo_svg: grp.logo_svg, color: client.color || grp.color };
  return { logo_svg: null, color: client.color || (sub && sub.color) || (grp && grp.color) || "#94a3b8" };
}

export default function CommandPalette({ onClose }) {
  const { items = [] } = useContext(DataStateContext) || {};
  const { globalFilter = {}, setGlobalFilter } = useContext(FilterContext) || {};
  const { canViewStage } = useContext(RoleContext) || {};
  const { user } = useContext(AuthContext) || {};
  const { clients = [] } = useContext(ClientsContext) || {};
  const { clientGroups = [] } = useContext(ClientGroupsContext) || {};
  const { theme, toggleTheme } = useContext(ThemeContext) || {};
  const { density, setDensity } = useTableDensity();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const activeRef = useRef(null);

  const [recentIds, setRecentIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY(user?.id)) || "[]"); } catch { return []; }
  });
  const pushRecent = useCallback((id) => {
    if (!id) return;
    setRecentIds(prev => {
      const next = [id, ...prev.filter(x => x !== id)].slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY(user?.id), JSON.stringify(next)); } catch {}
      return next;
    });
  }, [user?.id]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach(c => { m[c.name] = resolveClientBadge(c, clientGroups); });
    return m;
  }, [clients, clientGroups]);

  const recentItems = useMemo(() => {
    if (query.trim()) return [];
    const byId = {}; items.forEach(i => { byId[i.id] = i; });
    return recentIds.map(id => byId[id]).filter(Boolean).slice(0, RECENT_MAX);
  }, [query, items, recentIds]);

  const clientMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const nameById = id => (clients.find(c => c.id === id) || {}).name;
    const out = [];
    clients.forEach(c => {
      if (c.name && c.name.toLowerCase().includes(q)) {
        const badge = resolveClientBadge(c, clientGroups);
        out.push({ label: c.name, names: [c.name], color: badge.color, logo_svg: badge.logo_svg, tag: "client" });
      }
    });
    (clientGroups || []).forEach(g => {
      if (!g.name || !g.name.toLowerCase().includes(q)) return;
      let names;
      if (g.type === "subgroup") names = (g.client_ids || []).map(nameById).filter(Boolean);
      else {
        const subs = (clientGroups || []).filter(sg => sg.type === "subgroup" && sg.parent_id === g.id);
        names = [...(g.client_ids || []).map(nameById).filter(Boolean),
                 ...subs.flatMap(sg => (sg.client_ids || []).map(nameById).filter(Boolean))];
      }
      names = Array.from(new Set(names));
      if (names.length) out.push({ label: g.name, names, color: g.color, logo_svg: g.logo_svg, tag: `${g.type === "subgroup" ? "subgroup" : "group"} · ${names.length}` });
    });
    return out.slice(0, 6);
  }, [query, clients, clientGroups]);

  const viewCommands = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [
      { id: "dark",    label: "Night mode",    hint: "N", isOn: theme === "dark",       toggle: () => toggleTheme && toggleTheme() },
      { id: "compact", label: "Compact rows", hint: "Z", isOn: density === "compact",  toggle: () => setDensity(density === "compact" ? "comfortable" : "compact") },
    ];
    return all.filter(v => !q || v.label.toLowerCase().includes(q));
  }, [query, theme, density, toggleTheme, setDensity]);

  const attentionCommands = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ATTENTION_COMMANDS.filter(a => !q || a.label.toLowerCase().includes(q)).map(a => ({ ...a, on: a.isOn(globalFilter || {}) }));
  }, [query, globalFilter]);

  const results = useMemo(() => {
    const q = query.toLowerCase();
    const list = [];
    if (!query.trim()) recentItems.forEach((item, i) => list.push({ kind: "recent", item, recentIndex: i }));
    clientMatches.forEach(c => list.push({ kind: "client", ...c }));
    if (q) {
      items.filter(i =>
        [i.part_number, i.po_number, String(i.line_number || ""), i.item_description, i.client]
          .some(f => String(f || "").toLowerCase().includes(q))
      ).slice(0, 6).forEach(item => list.push({ kind: "item", item }));
    }
    attentionCommands.forEach(a => list.push({ kind: "attention", ...a }));
    FILTER_COMMANDS.filter(f => !q || f.label.toLowerCase().includes(q))
      .forEach(f => list.push({ kind: "filter", label: f.label, run: f.run, hint: f.hint }));
    viewCommands.forEach(v => list.push({ kind: "view", ...v }));
    const isAdmin = user?.role === "Admin";
    GOTO_ITEMS.filter(g => {
      if (q && !g.label.toLowerCase().includes(q)) return false;
      if (!g.stage) return true;
      if (isAdmin) return true;
      return canViewStage ? canViewStage(g.stage) : false;
    }).forEach(g => list.push({ kind: "goto", ...g }));
    // Pinned "Jump to" entry when query is a stage shortcut code (e.g. "gm", "g m")
    const norm = q.replace(/\s+/g, "");
    const sc = norm.match(/^g([hrpmqdcl])$/);
    if (sc) {
      const path = STAGE_SHORTCUT[sc[1]];
      const entry = GOTO_ITEMS.find(g => g.path === path);
      if (entry) {
        const allowed = !entry.stage || user?.role === "Admin" || (canViewStage ? canViewStage(entry.stage) : false);
        if (allowed) list.unshift({ kind: "pinned", ...entry });
      }
    }
    return list;
  }, [query, items, user, canViewStage, recentItems, clientMatches, viewCommands, attentionCommands]);

  useEffect(() => { setActiveIndex(idx => Math.min(idx, Math.max(0, results.length - 1))); }, [results.length]);
  useEffect(() => { activeRef.current?.scrollIntoView({ block: "nearest" }); }, [activeIndex]);

  const select = (result) => {
    if (!result) return;
    if (result.kind === "item" || result.kind === "recent") {
      const path = STAGE_PATHS[result.item.stage];
      const term = result.item.part_number || result.item.po_number || result.item.item_description || "";
      pushRecent(result.item.id);
      if (path) navigate(path, { state: { highlightId: result.item.id, search: term, ts: Date.now() } });
      onClose();
    } else if (result.kind === "goto" || result.kind === "pinned") {
      navigate(result.path); onClose();
    } else if (result.kind === "client") {
      setGlobalFilter(f => ({ ...f, clients: result.names })); onClose();
    } else if (result.kind === "attention") {
      setGlobalFilter(result.apply); onClose();
    } else if (result.kind === "view") {
      result.toggle();
      return;            // keep the palette open
    } else if (result.kind === "filter") {
      result.run(setGlobalFilter); onClose();
    }
  };

  const onKeyDown = (e) => {
    // "/" toggles the palette shut when the search box is empty (mirrors the "/"
    // that opens it). With text present, "/" types normally so it can't eat a
    // real search character.
    if (e.key === "/" && !query && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault(); onClose(); return;
    }
    if (!query.trim() && !e.metaKey && !e.ctrlKey && !e.altKey && /^[1-9]$/.test(e.key)) {
      const n = parseInt(e.key, 10) - 1;
      if (recentItems[n]) { e.preventDefault(); select({ kind: "recent", item: recentItems[n] }); return; }
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); select(results[activeIndex]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  const sections = useMemo(() => {
    const groups = []; let idx = 0;
    const push = (title, kind) => {
      const rows = results.filter(r => r.kind === kind).map(r => ({ result: r, flatIdx: idx++ }));
      if (rows.length) groups.push({ title, rows });
    };
    push("Jump to", "pinned"); push("Recent", "recent"); push("Clients", "client"); push("Items", "item"); push("Attention", "attention"); push("Filters", "filter"); push("View", "view"); push("Go to", "goto");
    return groups;
  }, [results]);

  return (
    <div className="relative w-full animate-in fade-in slide-in-from-top-1 duration-150" data-testid="command-palette">
      {/* Floating pill (2-Nov-2026) — same pill language as the header
          controls, orange ring + soft lift; the page dims behind it. */}
      <div className="rl-palette-pill rounded-full">
        <div className="h-[46px] flex items-center gap-2.5 pl-4 pr-2 rounded-full bg-[hsl(var(--card))]">
          <Search className="w-4 h-4 shrink-0 text-[#f97316]" strokeWidth={2.2} />
          <input
            ref={inputRef} type="text" value={query}
            onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
            onKeyDown={onKeyDown}
            placeholder="Search items, PO#, client, or jump to…"
            className="flex-1 bg-transparent text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none min-w-0"
            data-testid="command-palette-input" aria-label="Command palette search" autoComplete="off" spellCheck={false}
          />
          <kbd className="hidden sm:inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-bold font-mono-data bg-[hsl(var(--card))] text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))] border-b-2 leading-none shrink-0">Esc</kbd>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--muted-foreground))]/60 hover:text-[hsl(var(--muted-foreground))] shrink-0"
            aria-label="Close"
            data-testid="command-palette-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div
        className="absolute top-full mt-2.5 left-0 right-0 z-50 max-h-[60vh] overflow-auto rounded-[18px] p-1.5 border border-[hsl(var(--border))] bg-[hsl(var(--card))] rl-palette-list animate-in fade-in slide-in-from-top-1 duration-150"
        data-testid="command-palette-dropdown"
      >
        {results.length === 0 ? (
          <div className="px-3 py-5 text-center text-xs text-[hsl(var(--muted-foreground))]">
            {query ? "No matches" : "Start typing to search items…"}
          </div>
        ) : (
          <div>
            {sections.map(section => (
              <div key={section.title}>
                <div className="px-2.5 pt-2.5 pb-1.5 text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))] font-bold select-none" style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}>
                  {section.title}
                </div>
                {section.rows.map(({ result, flatIdx }) => {
                  const isActive = flatIdx === activeIndex;
                  return (
                    <div
                      key={flatIdx}
                      ref={isActive ? activeRef : null}
                      onMouseEnter={() => setActiveIndex(flatIdx)}
                      onClick={() => select(result)}
                      className={`flex items-center gap-2.5 px-2.5 h-9 rounded-[10px] cursor-pointer transition-colors ${isActive ? "bg-[#f97316]/10 text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50"}`}
                      data-testid={
                        (result.kind === "item" || result.kind === "recent") ? `cmd-item-${result.item.id}`
                        : (result.kind === "goto" || result.kind === "pinned") ? `cmd-goto-${result.path.replace(/\//g, "") || "dashboard"}`
                        : result.kind === "client" ? `cmd-client-${result.label.toLowerCase().replace(/\s+/g, "-")}`
                        : result.kind === "view" ? `cmd-view-${result.id}`
                        : result.kind === "attention" ? `cmd-attention-${result.id}`
                        : `cmd-filter-${result.label.toLowerCase().replace(/\s+/g, "-")}`
                      }
                    >
                      {(result.kind === "item" || result.kind === "recent") && <ItemRow item={result.item} clientMap={clientMap} recent={result.kind === "recent"} hint={result.kind === "recent" && result.recentIndex < 9 ? `${result.recentIndex + 1}` : null} />}
                      {result.kind === "client" && <ClientRow result={result} />}
                      {result.kind === "goto"   && <GotoRow   result={result} />}
                      {result.kind === "pinned" && <GotoRow   result={result} />}
                      {result.kind === "filter" && <FilterRow result={result} />}
                      {result.kind === "view"      && <ViewRow      result={result} />}
                      {result.kind === "attention" && <AttentionRow result={result} />}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemRow({ item, clientMap, recent = false, hint = null }) {
  const color = STAGE_COLORS[item.stage] || "#64748b";
  const ci = clientMap[item.client] || {};
  return (
    <>
      {recent ? <Clock className="w-3.5 h-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" /> : <Package className="w-3.5 h-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />}
      <span className="flex-1 text-sm truncate min-w-0">{item.item_description || "(no description)"}</span>
      <span className="text-[11px] font-mono-data text-[hsl(var(--muted-foreground))] shrink-0 hidden sm:inline">
        {[item.part_number, item.line_number != null ? `LN ${item.line_number}` : null].filter(Boolean).join(" · ")}
      </span>
      {item.client && (
        <span className="hidden md:flex items-center gap-1 text-[11px] text-[hsl(var(--muted-foreground))] shrink-0 max-w-[120px]">
          {ci.logo_svg
            ? <span
                className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
                style={{ width: 14, height: 14 }}
                dangerouslySetInnerHTML={{ __html: ci.logo_svg }}
              />
            : <span className="rounded-full shrink-0" style={{ width: 7, height: 7, background: ci.color || "#94a3b8" }} />
          }
          <span className="truncate">{item.client}</span>
        </span>
      )}
      <span
        className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded"
        style={{ background: color + "22", color, border: `1px solid ${color}44` }}
      >
        {item.stage === "Quality Control" ? "QC" : item.stage === "PO Received" ? "PO Rcvd" : item.stage}
      </span>
      {hint && <kbd className="text-[10px] font-medium font-[inherit] text-[hsl(var(--muted-foreground))]/70 shrink-0 ml-1">{hint}</kbd>}
    </>
  );
}

function GotoRow({ result }) {
  const Icon = result.icon;
  const color = result.stage ? STAGE_COLORS[result.stage] : null;
  return (
    <>
      <Icon
        className={`w-3.5 h-3.5 shrink-0${!color ? " text-[hsl(var(--muted-foreground))]" : ""}`}
        style={color ? { color } : undefined}
      />
      <span className="flex-1 text-sm">{result.label}</span>
      {result.hint && (
        <kbd className="text-[10px] font-medium font-[inherit] text-[hsl(var(--muted-foreground))]/70 shrink-0">
          {result.hint}
        </kbd>
      )}
    </>
  );
}

function FilterRow({ result }) {
  return (
    <>
      <SlidersHorizontal className="w-3.5 h-3.5 shrink-0 text-[#f97316]" />
      <span className="flex-1 text-sm">{result.label}</span>
      {result.hint
        ? <kbd className="text-[10px] font-medium font-[inherit] text-[hsl(var(--muted-foreground))]/70 shrink-0">{result.hint}</kbd>
        : <Filter className="w-3 h-3 shrink-0 text-[hsl(var(--muted-foreground))]/40" />}
    </>
  );
}

function ClientRow({ result }) {
  return (
    <>
      {result.logo_svg
        ? <span className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full" style={{ width: 16, height: 16 }} dangerouslySetInnerHTML={{ __html: result.logo_svg }} />
        : <span className="rounded-full shrink-0" style={{ width: 9, height: 9, background: result.color || "#94a3b8" }} />}
      <span className="flex-1 text-sm truncate min-w-0">Filter by <span className="font-semibold">{result.label}</span></span>
      <span className="shrink-0 text-[10px] text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border))] rounded px-1.5 py-0.5">{result.tag}</span>
    </>
  );
}

function ViewRow({ result }) {
  const Icon = result.id === "dark" ? Moon : AlignJustify;
  return (
    <>
      <Icon className="w-3.5 h-3.5 shrink-0 text-[hsl(var(--muted-foreground))]" />
      <span className="flex-1 text-sm">{result.label}</span>
      <span className={`shrink-0 text-[10px] font-medium rounded-full px-2 py-0.5 border ${result.isOn ? "border-[#f97316]/40 bg-[#f97316]/10 text-[#f97316]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
        {result.isOn ? "On" : "Off"}
      </span>
      {result.hint && <kbd className="text-[10px] font-medium font-[inherit] text-[hsl(var(--muted-foreground))]/70 shrink-0 ml-1">{result.hint}</kbd>}
    </>
  );
}

function AttentionRow({ result }) {
  const Icon = result.id === "blocked" ? ShieldAlert : result.id === "overdue" ? Clock : Zap;
  const color = result.id === "blocked" ? "#ef4444" : result.id === "overdue" ? "#ea580c" : "#f97316";
  return (
    <>
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <span className="flex-1 text-sm">{result.label}</span>
      {result.on && <span className="shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: color }} />}
      {result.hint && <kbd className="text-[10px] font-medium font-[inherit] text-[hsl(var(--muted-foreground))]/70 shrink-0 ml-1">{result.hint}</kbd>}
    </>
  );
}
